import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { FadeIn } from '@/components/motion/FadeIn';
import { TextSplit, Parallax } from '@/components/motion/Reveal';
import type { HomeHero as HomeHeroData } from '@/lib/cms-types';

interface Props {
  data: HomeHeroData;
  brandTagline: string;
}

const DEFAULTS: HomeHeroData = {
  headline: 'We build digital products that move Africa forward.',
  sub: 'Vonlof is a Ghana-based software studio designing and engineering products for ambitious teams across the continent — and the world.',
  primary_cta_label: 'View Projects',
  primary_cta_href: '/projects',
  secondary_cta_label: 'Contact Us',
  secondary_cta_href: '/contact',
  show_scroll_cue: true,
  background_variant: 'gradient',
  eyebrow: '',
};

export function HomeHero({ data, brandTagline }: Props) {
  const hero = { ...DEFAULTS, ...data };
  const words = hero.headline.split(' ');
  const accentFrom = Math.ceil(words.length / 2);
  return (
    <section className="relative isolate overflow-hidden">
      <div aria-hidden className="absolute inset-0 -z-10 gradient-hero" />

      {/* Parallax gold orbs — drift slower than scroll for depth */}
      <div aria-hidden className="absolute inset-0 -z-10 overflow-hidden opacity-60">
        <Parallax speed={-0.35} drift={20} className="absolute left-[12%] top-[18%] h-72 w-72">
          <div className="h-full w-full rounded-full bg-gold-500/15 blur-3xl animate-drift-slow" />
        </Parallax>
        <Parallax speed={-0.5} drift={-25} className="absolute right-[14%] top-[26%]">
          <div
            className="h-64 w-64 rounded-full bg-gold-500/10 blur-3xl animate-drift-slow"
            style={{ animationDelay: '-4s' }}
          />
        </Parallax>
        <Parallax speed={-0.25} drift={10} className="absolute left-1/2 top-[60%] -translate-x-1/2">
          <div className="h-48 w-[120%] bg-gradient-to-r from-transparent via-gold-500/8 to-transparent blur-3xl" />
        </Parallax>
      </div>

      <div className="container-default flex min-h-[80vh] flex-col justify-center pt-24 pb-16 sm:min-h-[88vh] sm:pt-28 sm:pb-20 md:pb-24">
        <FadeIn delay={0.05}>
          <Badge variant="gold" className="mb-6">
            <Sparkles className="h-3 w-3" />
            {hero.eyebrow || brandTagline || 'Headquartered in Ghana · Serving the world'}
          </Badge>
        </FadeIn>

        <TextSplit
          text={hero.headline}
          className="display-serif text-display-xl text-fg"
          accentFrom={accentFrom}
          delay={0.15}
          stagger={0.07}
        />

        <FadeIn delay={0.55} y={20}>
          <p className="mt-6 max-w-2xl text-base text-fg-muted sm:mt-8 sm:text-lg md:text-xl">{hero.sub}</p>
        </FadeIn>

        <FadeIn delay={0.7} y={24}>
          <div className="mt-8 flex flex-col gap-3 sm:mt-10 sm:flex-row sm:flex-wrap">
            <Button asChild size="xl">
              <Link to={hero.primary_cta_href}>
                {hero.primary_cta_label}
                <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
            {hero.secondary_cta_label && hero.secondary_cta_href ? (
              <Button asChild size="xl" variant="secondary">
                <Link to={hero.secondary_cta_href}>{hero.secondary_cta_label}</Link>
              </Button>
            ) : null}
          </div>
        </FadeIn>

        {hero.show_scroll_cue && (
          <FadeIn delay={0.95}>
            <div className="mt-12 flex items-center gap-3 text-sm text-fg-subtle sm:mt-20">
              <span>Scroll to explore</span>
              <span aria-hidden className="relative h-px w-16 overflow-hidden bg-gold-500/20">
                <span className="absolute inset-y-0 left-0 w-1/3 animate-scroll-cue bg-gradient-to-r from-transparent via-gold-500 to-transparent" />
              </span>
            </div>
          </FadeIn>
        )}
      </div>

      {/* Bottom seam — fades into the next section */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-b from-transparent to-bg"
      />
    </section>
  );
}
